import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { clientApi } from '../lib/api';
import { formatPrice, formatDate } from '../lib/utils';
import { CreditCard, Plus, Trash2, AlertTriangle, Check, ArrowLeft, Clock, RefreshCw, Bell, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Logo from '../components/Logo';

// Initialize Stripe - will be set dynamically
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured');
      return null;
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

// Card Form Component
function CardForm({ onSuccess, onCancel, customerId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      // Attach to customer
      await stripe.attachPaymentMethod(paymentMethod.id, {
        customer: customerId,
      });

      // Confirm with backend
      await clientApi.confirmPaymentMethod({
        payment_method_id: paymentMethod.id,
        customer_id: customerId
      });

      toast.success('Carte enregistrée avec succès !');
      onSuccess();
    } catch (err) {
      console.error('Error saving card:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        color: '#2A312C',
        fontFamily: '"Work Sans", sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#5C6A61',
        },
      },
      invalid: {
        color: '#EF4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-[#E3DED5] rounded-xl bg-white">
        <CardElement options={cardStyle} />
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}
      
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline flex-1"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="btn-primary flex-1"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}

export default function ClientPaymentPage() {
  const { user } = useAuth();
  const { config } = useConfig();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentFailures, setPaymentFailures] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [setupIntent, setSetupIntent] = useState(null);
  const [regularizing, setRegularizing] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pmRes, historyRes, failuresRes, notifRes] = await Promise.all([
        clientApi.getPaymentMethod(),
        clientApi.getPaymentHistory(),
        clientApi.getPaymentFailures(),
        clientApi.getNotifications()
      ]);
      
      setPaymentMethod(pmRes.data);
      setPaymentHistory(historyRes.data);
      setPaymentFailures(failuresRes.data);
      setNotifications(notifRes.data.filter(n => n.type.includes('payment')));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupCard = async () => {
    try {
      const response = await clientApi.setupPaymentMethod();
      setSetupIntent(response.data);
      setShowAddCard(true);
    } catch (error) {
      toast.error('Erreur lors de la configuration');
    }
  };

  const handleDeleteCard = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) return;
    
    try {
      await clientApi.deletePaymentMethod();
      setPaymentMethod({ has_card: false });
      toast.success('Carte supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRegularize = async (failureId) => {
    setRegularizing(failureId);
    try {
      await clientApi.regularizePayment(failureId);
      toast.success('Paiement régularisé !');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setRegularizing(null);
    }
  };

  const getCardBrandIcon = (brand) => {
    const brands = {
      visa: '💳 Visa',
      mastercard: '💳 Mastercard',
      amex: '💳 Amex',
      default: '💳'
    };
    return brands[brand?.toLowerCase()] || brands.default;
  };

  const getStatusBadge = (status) => {
    const statuses = {
      paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      failed: { label: 'Échoué', color: 'bg-red-100 text-red-800' },
      refunded: { label: 'Remboursé', color: 'bg-purple-100 text-purple-800' },
    };
    const s = statuses[status] || statuses.pending;
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/client" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C]">
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </Link>
            <Logo />
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A312C] mb-8">
          Paiement & Facturation
        </h1>

        {/* Payment Failures Alert */}
        {paymentFailures.length > 0 && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="payment-failures-alert">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="font-semibold text-red-800 mb-2">Paiement en échec</h2>
                {paymentFailures.map((failure) => (
                  <div key={failure.failure_id} className="mb-3 p-3 bg-white rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-red-700">
                          Réservation du {failure.reservation?.date}
                        </p>
                        <p className="text-lg font-semibold text-red-800">
                          {formatPrice(failure.amount)}
                        </p>
                        <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          Deadline: {new Date(failure.deadline).toLocaleString('fr-FR')}
                        </p>
                        <p className="text-xs text-[#5C6A61]">
                          Tentatives: {failure.attempts_count}/3
                        </p>
                      </div>
                      <button
                        onClick={() => handleRegularize(failure.failure_id)}
                        disabled={regularizing === failure.failure_id || !paymentMethod?.has_card}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                        data-testid={`regularize-btn-${failure.failure_id}`}
                      >
                        {regularizing === failure.failure_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Régulariser'
                        )}
                      </button>
                    </div>
                    {!paymentMethod?.has_card && (
                      <p className="text-xs text-red-600 mt-2">
                        ⚠️ Ajoutez une carte pour régulariser
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Saved Card */}
        <div className="card-default mb-8" data-testid="saved-card-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#2A312C] flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Ma carte de paiement
            </h2>
            {paymentMethod?.has_card && (
              <button
                onClick={handleDeleteCard}
                className="text-red-500 hover:bg-red-50 p-2 rounded-lg"
                title="Supprimer"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>

          {paymentMethod?.has_card ? (
            <div className="p-4 bg-[#F9F6F0] rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-8 bg-gradient-to-r from-[#2A312C] to-[#5C6A61] rounded flex items-center justify-center text-white text-xs">
                  {paymentMethod.card_brand?.toUpperCase()?.slice(0, 4)}
                </div>
                <div>
                  <p className="font-medium text-[#2A312C]">
                    •••• •••• •••• {paymentMethod.card_last4}
                  </p>
                  <p className="text-sm text-[#5C6A61]">
                    Expire {paymentMethod.card_exp_month}/{paymentMethod.card_exp_year}
                  </p>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800">Carte par défaut</Badge>
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
              <p className="text-[#5C6A61] mb-4">Aucune carte enregistrée</p>
              <button
                onClick={handleSetupCard}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="add-card-btn"
              >
                <Plus className="w-5 h-5" />
                Ajouter une carte
              </button>
            </div>
          )}

          {paymentMethod?.has_card && (
            <button
              onClick={handleSetupCard}
              className="mt-4 text-sm text-[#A5BBA8] hover:underline"
            >
              Modifier ma carte
            </button>
          )}
        </div>

        {/* Payment History */}
        <div className="card-default" data-testid="payment-history-section">
          <h2 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Historique des paiements
          </h2>

          {paymentHistory.length === 0 ? (
            <p className="text-center py-8 text-[#5C6A61]">Aucun paiement</p>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((txn) => (
                <div
                  key={txn.transaction_id}
                  className="flex items-center justify-between p-3 bg-[#F9F6F0] rounded-lg"
                >
                  <div>
                    <p className="font-medium text-[#2A312C]">
                      {txn.reservation_date ? formatDate(txn.reservation_date) : 'Paiement'}
                    </p>
                    <p className="text-sm text-[#5C6A61]">
                      {new Date(txn.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#2A312C]">{formatPrice(txn.amount)}</p>
                    {getStatusBadge(txn.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Notifications */}
        {notifications.length > 0 && (
          <div className="card-default mt-8" data-testid="notifications-section">
            <h2 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications récentes
            </h2>
            <div className="space-y-2">
              {notifications.slice(0, 5).map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`p-3 rounded-lg ${notif.is_read ? 'bg-[#F9F6F0]' : 'bg-[#E8B25C]/10 border border-[#E8B25C]/30'}`}
                >
                  <p className="font-medium text-[#2A312C]">{notif.title}</p>
                  <p className="text-sm text-[#5C6A61]">{notif.message}</p>
                  <p className="text-xs text-[#5C6A61] mt-1">
                    {new Date(notif.created_at).toLocaleString('fr-FR')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Add Card Dialog */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              {paymentMethod?.has_card ? 'Modifier ma carte' : 'Ajouter une carte'}
            </DialogTitle>
          </DialogHeader>
          
          {setupIntent && (
            <Elements stripe={getStripe()}>
              <CardForm
                customerId={setupIntent.customer_id}
                onSuccess={() => {
                  setShowAddCard(false);
                  loadData();
                }}
                onCancel={() => setShowAddCard(false)}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
